import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Shield, Package, Thermometer, Lock } from 'lucide-react';

const products = [
  {
    id: 'trackotic',
    name: 'TrackOtic™',
    tagline: 'Delivery Assurance & Compliance Platform',
    description: 'Secure, compliant, and auditable delivery of pharmaceuticals with smart IoT tracking and real-time cold chain monitoring.',
    image: 'https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&q=85',
    icon: Shield,
    features: ['Cold Chain Compliance', 'Real-time Tracking', 'Tamper Detection', 'Regulatory Documentation'],
    industries: ['Pharmaceuticals', 'Vaccines', 'Biologics'],
    status: 'flagship'
  }
];

export default function ProductsPage() {
  return (
    <div data-testid="products-page" className="min-h-screen pt-16 bg-white">
      {/* Hero */}
      <section className="py-20 gradient-hero">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Badge className="mb-6 bg-blue-100 text-blue-700 border-blue-200 font-medium">
              OUR PRODUCTS
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Intelligent Solutions
              <span className="text-primary"> For Critical Operations</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
              Purpose-built platforms that combine IoT hardware, real-time monitoring, 
              and regulatory compliance to secure your most valuable assets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <Link to={`/products/${product.id}`}>
                  <div className="grid lg:grid-cols-2 gap-8 bg-white rounded-2xl border border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 overflow-hidden">
                    {/* Image */}
                    <div className="aspect-video lg:aspect-auto relative overflow-hidden">
                      <img 
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-transparent" />
                      {product.status === 'flagship' && (
                        <Badge className="absolute top-4 left-4 bg-primary text-white border-0">
                          FLAGSHIP PRODUCT
                        </Badge>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-8 flex flex-col justify-center">
                      <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                        <product.icon className="w-7 h-7 text-primary" />
                      </div>
                      
                      <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                        {product.name}
                      </h2>
                      <p className="text-primary font-medium mb-4">{product.tagline}</p>
                      <p className="text-gray-600 mb-6">{product.description}</p>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-6">
                        {product.features.map((feature) => (
                          <span 
                            key={feature}
                            className="px-3 py-1 bg-gray-100 text-sm text-gray-700 rounded-full"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      {/* Industries */}
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                        <span className="text-xs uppercase tracking-wider font-medium">Industries:</span>
                        {product.industries.map((industry, i) => (
                          <span key={industry}>
                            {industry}{i < product.industries.length - 1 && ' •'}
                          </span>
                        ))}
                      </div>

                      <Button 
                        className="w-fit bg-primary hover:bg-blue-700 text-white font-semibold rounded-lg"
                        data-testid={`explore-${product.id}-btn`}
                      >
                        Explore {product.name}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Badge className="mb-4 bg-gray-100 text-gray-700 border-gray-200">
              COMING SOON
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              More Products in Development
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
              Our R&D team is working on next-generation solutions for industrial IoT, 
              environmental monitoring, and infrastructure security.
            </p>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { icon: Package, name: 'SmartVault', desc: 'Industrial asset tracking' },
                { icon: Thermometer, name: 'EnviroSense', desc: 'Environmental monitoring' },
                { icon: Lock, name: 'SecureEdge', desc: 'Edge computing security' },
              ].map((item) => (
                <div 
                  key={item.name}
                  className="p-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200"
                >
                  <item.icon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                  <h4 className="font-heading font-semibold text-gray-500">{item.name}</h4>
                  <p className="text-xs text-gray-400">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Need a Custom Solution?
          </h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            Our engineering team specializes in building tailored IoT and software solutions 
            for enterprise requirements.
          </p>
          <Link to="/contact">
            <Button 
              size="lg"
              className="bg-primary hover:bg-blue-700 text-white font-semibold h-14 px-8 rounded-lg"
            >
              Contact Our Team
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
